import React from 'react';
import { Badge, Button, Drawer, Form, Input, List, message, Space, Tag, Tooltip } from 'antd';
import { CustomerServiceOutlined, SendOutlined } from '@ant-design/icons';
import { useLocation } from 'react-router-dom';
import api, { supportAPI } from '../services/api';
import { useAuthStore } from '../store/authStore';
import { useIsMobile } from '../hooks/useIsMobile';

type GuestSession = {
  guestToken: string;
  ticketId: number;
};

function loadGuestSession(): GuestSession | null {
  try {
    const raw = localStorage.getItem('supportGuestSession');
    if (!raw) return null;
    const v = JSON.parse(raw);
    if (!v || typeof v.guestToken !== 'string' || typeof v.ticketId !== 'number') return null;
    return v;
  } catch {
    return null;
  }
}

function saveGuestSession(s: GuestSession) {
  localStorage.setItem('supportGuestSession', JSON.stringify(s));
}

export default function SupportWidget() {
  const { user, token, hydrated } = useAuthStore();
  const isMobile = useIsMobile();
  const location = useLocation();
  const hideOnPage = ['/login', '/register', '/register-support', '/password-reset', '/forgot-credentials', '/'].includes(
    location.pathname
  );
  const [open, setOpen] = React.useState(false);
  const [loading, setLoading] = React.useState(false);
  const [ticket, setTicket] = React.useState<any | null>(null);
  const [unread, setUnread] = React.useState(0);

  const [guestForm] = Form.useForm();
  const [reply, setReply] = React.useState('');

  // While session is verifying, do not fall back to guest chat
  const isAuthed = Boolean(user);
  const guestSession = React.useMemo(
    () => (!isAuthed && hydrated && !token ? loadGuestSession() : null),
    [isAuthed, hydrated, token]
  );

  const load = React.useCallback(async () => {
    if (!hydrated) return;
    setLoading(true);
    try {
      if (isAuthed) {
        const items = await supportAPI.getTickets({ archived: false });
        const first = items?.[0] || null;
        setTicket(first);
      } else if (guestSession?.guestToken) {
        const t = await api.get(`/api/support/public/tickets/${guestSession.guestToken}`).then((r) => r.data);
        setTicket(t);
      } else {
        setTicket(null);
      }
    } catch (e: any) {
      // don't spam toast on background refresh
      if (open) message.error(e?.response?.data?.error || 'Не удалось загрузить чат');
    } finally {
      setLoading(false);
    }
  }, [guestSession?.guestToken, isAuthed, open, hydrated]);

  React.useEffect(() => {
    load();
  }, [load]);

  // Poll updates
  React.useEffect(() => {
    const id = setInterval(() => load(), 30000);
    return () => clearInterval(id);
  }, [load]);

  // Unread counter (very simple heuristic)
  React.useEffect(() => {
    const msgs = ticket?.messages || [];
    const fromSupport = msgs.filter((m: any) => m && !m.isFromUser).length;
    setUnread(open ? 0 : fromSupport ? 1 : 0);
  }, [ticket, open]);

  const ensureGuestTicket = async (): Promise<{ guestToken: string; ticketId: number }> => {
    const existing = loadGuestSession();
    if (existing) return existing;

    const values = await guestForm.validateFields();
    const payload = {
      subject: values.subject || 'Обращение с сайта',
      message: values.message,
      name: values.name,
      email: values.email,
      phone: values.phone,
      url: window.location.href,
      userAgent: navigator.userAgent,
    };
    const res = await api.post('/api/support/public/tickets', payload).then((r) => r.data);
    const session = { guestToken: res.guestToken, ticketId: res.ticketId };
    saveGuestSession(session);
    setTicket(res.ticket);
    guestForm.resetFields(['message']);
    return session;
  };

  const send = async () => {
    if (!reply.trim()) {
      message.warning('Введите сообщение');
      return;
    }

    try {
      if (isAuthed) {
        if (!ticket) {
          // create first ticket for authed user
          const created = await supportAPI.createTicket({ subject: 'Обращение', message: reply.trim() });
          setTicket(created);
          setReply('');
          return;
        }
        await supportAPI.replyTicket(ticket.id, reply.trim());
      } else {
        const session = await ensureGuestTicket();
        await api.post(`/api/support/public/tickets/${session.guestToken}/reply`, { message: reply.trim() });
      }
      setReply('');
      await load();
    } catch (e: any) {
      message.error(e?.response?.data?.error || 'Не удалось отправить сообщение');
    }
  };

  const headerTitle = (
    <Space size="small">
      <CustomerServiceOutlined />
      <span>Поддержка</span>
      {isAuthed ? <Tag color="blue">вход выполнен</Tag> : <Tag>гость</Tag>}
    </Space>
  );

  if (!hydrated || hideOnPage) return null;

  return (
    <>
      <Tooltip title="Поддержка">
        <Badge count={unread} offset={[-2, 6]}>
          <Button
            type="primary"
            shape="circle"
            icon={<CustomerServiceOutlined />}
            onClick={() => setOpen(true)}
            className="support-widget-fab"
            style={{
              position: 'fixed',
              right: 16,
              bottom: 16,
              width: 52,
              height: 52,
              zIndex: 2000,
              boxShadow: '0 14px 40px rgba(16,24,40,0.20)',
            }}
          />
        </Badge>
      </Tooltip>

      <Drawer
        title={headerTitle}
        open={open}
        onClose={() => setOpen(false)}
        width={isMobile ? '100%' : 420}
        destroyOnClose={false}
      >
        {!isAuthed && !guestSession ? (
          <Form layout="vertical" form={guestForm}>
            <Form.Item label="Тема" name="subject">
              <Input placeholder="Коротко о проблеме (необязательно)" />
            </Form.Item>
            <Form.Item label="Имя" name="name">
              <Input placeholder="Как к вам обращаться (необязательно)" />
            </Form.Item>
            <Form.Item
              label="Email"
              name="email"
              rules={[
                {
                  validator: async (_, v) => {
                    const phone = guestForm.getFieldValue('phone');
                    if ((v && String(v).trim()) || (phone && String(phone).trim())) return;
                    throw new Error('Укажите email или телефон');
                  },
                },
              ]}
            >
              <Input placeholder="email для ответа" />
            </Form.Item>
            <Form.Item
              label="Телефон"
              name="phone"
              rules={[
                {
                  validator: async (_, v) => {
                    const email = guestForm.getFieldValue('email');
                    if ((v && String(v).trim()) || (email && String(email).trim())) return;
                    throw new Error('Укажите email или телефон');
                  },
                },
              ]}
            >
              <Input placeholder="+7..." />
            </Form.Item>
            <Form.Item
              label="Сообщение"
              name="message"
              rules={[{ required: true, message: 'Введите сообщение' }]}
            >
              <Input.TextArea rows={4} placeholder="Опишите, что не работает / что нужно улучшить" />
            </Form.Item>
            <Button type="primary" onClick={async () => ensureGuestTicket()} loading={loading} block>
              Начать чат
            </Button>
          </Form>
        ) : (
          <>
            <div
              style={{
                border: '1px solid rgba(0,0,0,0.06)',
                borderRadius: 12,
                padding: 12,
                background: 'rgba(0,0,0,0.02)',
                marginBottom: 12,
                maxHeight: 360,
                overflowY: 'auto',
              }}
            >
              <List
                loading={loading}
                dataSource={ticket?.messages || []}
                locale={{ emptyText: 'Сообщений пока нет' }}
                renderItem={(m: any, idx: number) => (
                  <List.Item key={idx} style={{ border: 'none', padding: '8px 0' }}>
                    <div style={{ width: '100%' }}>
                      <div style={{ fontSize: 12, color: '#667085', marginBottom: 4 }}>
                        {m.isFromUser ? 'Вы' : 'Поддержка'} · {new Date(m.createdAt).toLocaleString('ru')}
                      </div>
                      <div
                        style={{
                          display: 'inline-block',
                          padding: '10px 12px',
                          borderRadius: 12,
                          background: m.isFromUser ? 'rgba(102,126,234,0.12)' : 'rgba(0,0,0,0.04)',
                          border: '1px solid rgba(0,0,0,0.06)',
                          maxWidth: '100%',
                          whiteSpace: 'pre-wrap',
                        }}
                      >
                        {m.message}
                      </div>
                    </div>
                  </List.Item>
                )}
              />
            </div>

            <Space.Compact style={{ width: '100%' }}>
              <Input
                placeholder="Напишите сообщение…"
                value={reply}
                onChange={(e) => setReply(e.target.value)}
                onPressEnter={(e) => {
                  if (!e.shiftKey) {
                    e.preventDefault();
                    void send();
                  }
                }}
              />
              <Button type="primary" icon={<SendOutlined />} onClick={() => void send()} />
            </Space.Compact>
            <div style={{ marginTop: 8, fontSize: 12, color: '#667085' }}>
              Отправляя сообщение, вы соглашаетесь на обработку данных для связи по обращению.
            </div>
          </>
        )}
      </Drawer>
    </>
  );
}

